import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronLeftIcon } from '@heroicons/react/24/outline';
import { getColor } from '../../theme/colors';
import {
  CANONICAL_SCHEMAS,
  CANONICAL_RELATIONSHIPS,
} from '../../mock_data/canonical_schema';
import ForceGraph from './ForceGraph';

// Map canonical domain → theme color name (theme colors only — never
// hardcode Tailwind class strings here).
const DOMAIN_COLORS = {
  core: 'brand',
  finance: 'teal',
  marketing: 'pink',
  sales: 'green',
  analytics: 'brightblue',
  hr: 'violet',
};

// Map canonical field type → theme color name for the type badge.
const TYPE_COLORS = {
  uuid: 'grey',
  string: 'grey',
  integer: 'brightblue',
  decimal: 'violet',
  float: 'violet',
  boolean: 'green',
  date: 'brand',
  timestamp: 'pink',
  enum: 'teal',
  fk: 'violet',
  json: 'darkblue',
};

const displayName = (t) => (t || '').replace(/^canonical[._]/, '');

function Pill({ colorName, children }) {
  const color = getColor(colorName);
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium ${color.bg} ${color.text}`}>
      {children}
    </span>
  );
}

function EntityRow({ schema, isOpen, onToggle, relationships }) {
  const domainColorName = DOMAIN_COLORS[schema.domain] || 'grey';
  const rels = relationships.filter((r) => r.from === schema.entity || r.to === schema.entity);
  const entityName = schema.entity || displayName(schema.bq_table || '');
  const fields = schema.fields || [];

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden bg-white">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition-colors"
        aria-expanded={isOpen}
      >
        <div className="flex items-center gap-3 min-w-0">
          <div className={`w-2.5 h-2.5 rounded-full ${getColor(domainColorName).bgSolid} flex-shrink-0`} />
          <div className="text-left min-w-0">
            <div className="text-sm font-semibold text-gray-800 truncate">{entityName}</div>
            <div className="flex items-center gap-1.5 mt-1 flex-wrap">
              <Pill colorName={domainColorName}>{schema.domain}</Pill>
              <span className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium bg-gray-100 text-gray-600">
                {fields.length} fields
              </span>
              {schema.version && (
                <span className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium bg-gray-100 text-gray-600">
                  v{schema.version}
                </span>
              )}
            </div>
          </div>
        </div>
        <span className="text-gray-400 text-lg ml-3 flex-shrink-0">{isOpen ? '−' : '+'}</span>
      </button>

      {isOpen && (
        <div className="border-t border-gray-100 bg-gray-50/50 px-4 py-4">
          {schema.description && (
            <p className="text-xs text-gray-600 leading-relaxed mb-3">{schema.description}</p>
          )}

          {schema.sources?.length > 0 && (
            <div className="flex items-center gap-1.5 flex-wrap mb-3">
              <span className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide">Sources:</span>
              {schema.sources.map((s) => (
                <span key={`${s.connector}-${s.raw_table}`} className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium bg-white border border-gray-200 text-gray-600">
                  {s.connector}<span className="text-gray-300 mx-1">·</span><span className="font-mono">{s.raw_table}</span>
                </span>
              ))}
            </div>
          )}

          {rels.length > 0 && (
            <div className="flex items-center gap-1.5 flex-wrap mb-3">
              <span className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide">Relationships:</span>
              {rels.map((r, i) => {
                const other = r.from === schema.entity ? r.to : r.from;
                return (
                  <Pill key={`${r.from}-${r.to}-${i}`} colorName="violet">
                    {r.label || r.join_key} → {other}
                  </Pill>
                );
              })}
            </div>
          )}

          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-gray-200 text-left">
                <th className="px-2 py-1.5 text-[11px] font-semibold text-gray-500 uppercase tracking-wide">Field</th>
                <th className="px-2 py-1.5 text-[11px] font-semibold text-gray-500 uppercase tracking-wide">Type</th>
                <th className="px-2 py-1.5 text-[11px] font-semibold text-gray-500 uppercase tracking-wide">Description</th>
              </tr>
            </thead>
            <tbody>
              {fields.map((f) => {
                const isFk = f.type === 'fk' || f.ref || f.references;
                return (
                  <tr key={f.name} className="border-b border-gray-100 last:border-0">
                    <td className={`px-2 py-1.5 font-mono ${isFk ? 'text-violet-700' : 'text-gray-700'} ${f.required ? 'font-semibold' : ''}`}>
                      {f.name}{f.required && <span className="text-red-400">*</span>}
                    </td>
                    <td className="px-2 py-1.5">
                      <Pill colorName={TYPE_COLORS[f.type] || 'grey'}>{f.type}</Pill>
                    </td>
                    <td className="px-2 py-1.5 text-gray-500 max-w-md">
                      {f.description || ''}
                      {(f.ref || f.references) && (
                        <span className="text-violet-600"> → {f.ref || f.references}</span>
                      )}
                      {f.values?.length > 0 && (
                        <div className="mt-1 flex flex-wrap gap-1">
                          {f.values.map((v) => (
                            <span key={v} className="inline-block bg-white border border-gray-200 px-1.5 py-0.5 rounded text-[10px] text-gray-500">
                              {v}
                            </span>
                          ))}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Side panel: shows selected node details on the graph ─────────────
function NodeDetailPanel({ schema, relationships, onClose }) {
  if (!schema) return null;
  const dc = DOMAIN_COLORS[schema.domain] || 'grey';
  const domainColor = getColor(dc);
  const fields = schema.fields || [];
  const rels = relationships.filter((r) => r.from === schema.entity || r.to === schema.entity);

  return (
    <div className="absolute top-2 right-2 bottom-2 w-[360px] bg-white border border-gray-200 rounded-xl shadow-xl overflow-y-auto z-20">
      <div className={`${domainColor.bgSolid} text-white px-4 py-3 flex items-center justify-between sticky top-0 z-10`}>
        <div className="min-w-0">
          <div className="text-base font-bold truncate">{schema.entity}</div>
          <div className="text-[11px] opacity-80">
            {schema.domain} · {fields.length} fields{schema.version ? ` · v${schema.version}` : ''}
          </div>
        </div>
        <button
          onClick={onClose}
          className="ml-2 w-7 h-7 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center text-base"
          aria-label="Close"
        >
          ×
        </button>
      </div>

      {schema.description && (
        <div className="px-4 py-3 text-xs text-gray-600 leading-relaxed border-b border-gray-100">
          {schema.description}
        </div>
      )}

      {rels.length > 0 && (
        <div className="px-4 py-3 border-b border-gray-100">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 mb-2">
            Relationships
          </div>
          <div className="space-y-1">
            {rels.map((r, i) => {
              const isOut = r.from === schema.entity;
              return (
                <div key={`${r.from}-${r.to}-${i}`} className="text-xs flex items-center gap-1.5 bg-violet-50 text-violet-700 rounded px-2 py-1">
                  <span className="font-mono text-[11px]">{isOut ? '→' : '←'}</span>
                  <span className="font-mono text-[11px]">{r.join_key}</span>
                  {r.label && <span className="text-violet-500">({r.label})</span>}
                  <span className="text-violet-400">·</span>
                  <span className="font-semibold">{isOut ? r.to : r.from}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="px-4 py-3">
        <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 mb-2">
          Attributes
        </div>
        <div className="space-y-1.5">
          {fields.map((f) => {
            const isFk = f.type === 'fk' || f.ref || f.references;
            return (
              <div key={f.name} className="py-1 border-b border-gray-50 last:border-0">
                <div className="flex items-center justify-between gap-2">
                  <span className={`font-mono text-xs ${isFk ? 'text-violet-700' : 'text-gray-700'} ${f.required ? 'font-semibold' : ''}`}>
                    {f.name}{f.required && <span className="text-red-400">*</span>}
                  </span>
                  <Pill colorName={TYPE_COLORS[f.type] || 'grey'}>{f.type}</Pill>
                </div>
                {(f.description || f.ref || f.references) && (
                  <div className="text-[11px] text-gray-500 mt-0.5">
                    {f.description}
                    {(f.ref || f.references) && (
                      <span className="text-violet-600"> → {f.ref || f.references}</span>
                    )}
                  </div>
                )}
                {f.values?.length > 0 && (
                  <div className="mt-1 flex flex-wrap gap-1">
                    {f.values.map((v) => (
                      <span key={v} className="inline-block bg-gray-50 border border-gray-200 px-1.5 py-0.5 rounded text-[10px] text-gray-500">
                        {v}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default function CanonicalModelTab({ onBack }) {
  const [view, setView] = useState('graph'); // 'graph' | 'list'
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState({});
  const [selectedNodeId, setSelectedNodeId] = useState(null);

  // Sizing for the canvas — measure container width.
  const graphContainerRef = useRef(null);
  const [dims, setDims] = useState({ w: 800, h: 600 });
  useEffect(() => {
    if (view !== 'graph') return;
    const update = () => {
      const el = graphContainerRef.current;
      if (el) {
        setDims({
          w: el.clientWidth,
          h: Math.max(520, window.innerHeight - 280),
        });
      }
    };
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, [view]);

  // ForceGraph expects `table` as node ID and `from_table`/`to_table` on
  // edges. Our JSONs use `entity` / `from` / `to`, so adapt at the
  // boundary — keeps ForceGraph reusable for both shapes.
  const { graphSchemas, graphRelationships } = useMemo(() => {
    const schemas = CANONICAL_SCHEMAS.map((s) => ({
      ...s,
      table: s.entity,
      field_count: s.fields?.length || 0,
    }));
    const rels = CANONICAL_RELATIONSHIPS.map((r) => ({
      ...r,
      from_table: r.from,
      to_table: r.to,
      from_field: r.join_key,
    }));
    return { graphSchemas: schemas, graphRelationships: rels };
  }, []);

  const selectedSchema = useMemo(
    () => CANONICAL_SCHEMAS.find((s) => s.entity === selectedNodeId) || null,
    [selectedNodeId]
  );

  const domains = useMemo(() => {
    const set = new Set(CANONICAL_SCHEMAS.map((s) => s.domain).filter(Boolean));
    return ['all', ...Array.from(set).sort()];
  }, []);

  const filtered = useMemo(() => {
    return CANONICAL_SCHEMAS.filter((s) => {
      if (filter !== 'all' && s.domain !== filter) return false;
      if (!search) return true;
      const q = search.toLowerCase();
      return (
        (s.entity || '').toLowerCase().includes(q) ||
        (s.bq_table || '').toLowerCase().includes(q)
      );
    });
  }, [filter, search]);

  const totalFields = useMemo(
    () => CANONICAL_SCHEMAS.reduce((sum, s) => sum + (s.fields?.length || 0), 0),
    []
  );

  return (
    <div className="space-y-4">
      <div className="mb-2 flex items-start justify-between gap-3">
        <div className="min-w-0">
          {onBack && (
            <button
              onClick={onBack}
              className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-gray-800 mb-2"
            >
              <ChevronLeftIcon className="w-3.5 h-3.5" />
              Back to Models
            </button>
          )}
          <h3 className="text-base font-semibold text-gray-800 mb-1">Pulse Canonical Schema</h3>
          <p className="text-xs text-gray-500">
            {CANONICAL_SCHEMAS.length} entities · {totalFields} fields · {CANONICAL_RELATIONSHIPS.length} relationships
          </p>
        </div>

        {/* Graph / List toggle */}
        <div className="inline-flex border border-gray-200 rounded-md overflow-hidden text-xs flex-shrink-0">
          {['graph', 'list'].map((v) => {
            const active = view === v;
            return (
              <button
                key={v}
                onClick={() => setView(v)}
                className={`px-4 py-1.5 font-medium transition-colors ${
                  active ? 'bg-gray-900 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
                }`}
              >
                {v === 'graph' ? 'Graph' : 'List'}
              </button>
            );
          })}
        </div>
      </div>

      {view === 'graph' ? (
        <div
          ref={graphContainerRef}
          className="relative border border-gray-200 rounded-lg overflow-hidden"
          style={{ height: dims.h }}
        >
          <ForceGraph
            schemas={graphSchemas}
            relationships={graphRelationships}
            onNodeClick={setSelectedNodeId}
            onEdgeClick={() => {}}
            onDoubleClickNode={() => {}}
            selectedNode={selectedNodeId}
            selectedEdgeIdx={null}
            hiddenNodes={[]}
            width={dims.w}
            height={dims.h}
          />

          {/* Domain legend */}
          <div className="absolute bottom-2 left-2 bg-white/95 backdrop-blur border border-gray-200 rounded-md px-3 py-1.5 flex items-center gap-3 text-[11px] text-gray-500">
            {Object.keys(DOMAIN_COLORS).map((d) => (
              <div key={d} className="flex items-center gap-1.5 capitalize">
                <div className={`w-2 h-2 rounded-full ${getColor(DOMAIN_COLORS[d]).bgSolid}`} />
                {d}
              </div>
            ))}
          </div>

          {selectedSchema && (
            <NodeDetailPanel
              schema={selectedSchema}
              relationships={CANONICAL_RELATIONSHIPS}
              onClose={() => setSelectedNodeId(null)}
            />
          )}
        </div>
      ) : (
        <>
          <div className="flex items-center gap-2 flex-wrap">
            {domains.map((d) => {
              const isActive = filter === d;
              const colorName = DOMAIN_COLORS[d] || 'grey';
              const color = getColor(colorName);
              return (
                <button
                  key={d}
                  onClick={() => setFilter(d)}
                  className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
                    isActive
                      ? `${color.bgSolid} text-white border-transparent`
                      : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  {d === 'all' ? 'All' : d}
                </button>
              );
            })}
            <input
              type="text"
              placeholder="Search entities…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="ml-auto px-3 py-1 text-xs border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-400 w-56"
            />
          </div>

          <div className="space-y-2">
            {filtered.map((schema) => (
              <EntityRow
                key={schema.entity || schema.bq_table}
                schema={schema}
                relationships={CANONICAL_RELATIONSHIPS}
                isOpen={Boolean(expanded[schema.entity])}
                onToggle={() =>
                  setExpanded((prev) => ({ ...prev, [schema.entity]: !prev[schema.entity] }))
                }
              />
            ))}
            {filtered.length === 0 && (
              <div className="text-center text-xs text-gray-400 py-8">
                No entities match the current filter.
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
